/**
 * SchemaTablePlugin — Plugin do Swagger UI que renderiza schemas como tabelas
 *
 * Substitui o componente padrão de renderização de modelos por uma tabela
 * clara e legível com colunas: Campo | Tipo | Obrigatório | Descrição | Exemplo
 */

import React from 'react';
import { Layers, ChevronUp, ChevronDown } from 'lucide-react';

// ── Tipos ─────────────────────────────────────────────────────────────────

interface SchemaProperty {
  type?: string;
  format?: string;
  description?: string;
  example?: any;
  enum?: any[];
  $ref?: string;
  items?: SchemaProperty;
  properties?: Record<string, SchemaProperty>;
  required?: string[];
  nullable?: boolean;
  readOnly?: boolean;
  writeOnly?: boolean;
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  default?: any;
}

interface SchemaObject {
  type?: string;
  description?: string;
  properties?: Record<string, SchemaProperty>;
  required?: string[];
  example?: any;
  enum?: any[];
  $ref?: string;
  allOf?: SchemaObject[];
  oneOf?: SchemaObject[];
  anyOf?: SchemaObject[];
}

// ── Helpers ───────────────────────────────────────────────────────────────

const TYPE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  string: { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
  number: { bg: '#faf5ff', text: '#7c3aed', border: '#e9d5ff' },
  integer: { bg: '#faf5ff', text: '#7c3aed', border: '#e9d5ff' },
  boolean: { bg: '#fffbeb', text: '#d97706', border: '#fde68a' },
  array: { bg: '#f0fdf4', text: '#15803d', border: '#bbf7d0' },
  object: { bg: '#fff1f2', text: '#be123c', border: '#fecdd3' },
};

function getTypeColor(type: string) {
  return TYPE_COLORS[type] || { bg: '#f9fafb', text: '#374151', border: '#e5e7eb' };
}

function resolveRef(ref: string): string {
  // "#/components/schemas/MySchema" → "MySchema"
  return ref.split('/').pop() || ref;
}

function getTypeLabel(prop: SchemaProperty): string {
  if (prop.$ref) return resolveRef(prop.$ref);
  if (prop.type === 'array') {
    if (prop.items?.$ref) return `array[${resolveRef(prop.items.$ref)}]`;
    if (prop.items?.type) return `array[${prop.items.type}]`;
    return 'array';
  }
  if (prop.format) return `${prop.type} (${prop.format})`;
  return prop.type || 'any';
}

function TypeBadge({ prop }: { prop: SchemaProperty }) {
  const type = prop.$ref ? 'object' : prop.type || 'any';
  const label = getTypeLabel(prop);
  const colors = getTypeColor(type);

  return (
    <span
      style={{
        display: 'inline-block',
        backgroundColor: colors.bg,
        color: colors.text,
        border: `1px solid ${colors.border}`,
        borderRadius: '4px',
        padding: '1px 7px',
        fontSize: '11px',
        fontWeight: 600,
        fontFamily: 'var(--font-mono, monospace)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

function EnumBadges({ values }: { values: any[] }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '4px' }}>
      {values.map((v, i) => (
        <span
          key={i}
          style={{
            backgroundColor: '#fef3c7',
            color: '#92400e',
            border: '1px solid #fde68a',
            borderRadius: '3px',
            padding: '0 5px',
            fontSize: '11px',
            fontFamily: 'var(--font-mono, monospace)',
          }}
        >
          {String(v)}
        </span>
      ))}
    </div>
  );
}

// ── Componente de tabela de schema ────────────────────────────────────────

function SchemaTable({ schema, depth = 0 }: { schema: SchemaObject; depth?: number }) {
  const properties = schema.properties || {};
  const required = schema.required || [];
  const entries = Object.entries(properties);

  if (entries.length === 0) {
    return (
      <p style={{ color: '#9ca3af', fontSize: '12px', padding: '8px 0', fontStyle: 'italic' }}>
        Sem propriedades definidas
      </p>
    );
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
      <thead>
        <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
          <th
            style={{
              padding: '8px 12px',
              textAlign: 'left',
              fontSize: '11px',
              fontWeight: 700,
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              width: '180px',
            }}
          >
            Campo
          </th>
          <th
            style={{
              padding: '8px 12px',
              textAlign: 'left',
              fontSize: '11px',
              fontWeight: 700,
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              width: '140px',
            }}
          >
            Tipo
          </th>
          <th
            style={{
              padding: '8px 12px',
              textAlign: 'center',
              fontSize: '11px',
              fontWeight: 700,
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              width: '80px',
            }}
          >
            Req.
          </th>
          <th
            style={{
              padding: '8px 12px',
              textAlign: 'left',
              fontSize: '11px',
              fontWeight: 700,
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Descrição
          </th>
          <th
            style={{
              padding: '8px 12px',
              textAlign: 'left',
              fontSize: '11px',
              fontWeight: 700,
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              width: '140px',
            }}
          >
            Exemplo
          </th>
        </tr>
      </thead>
      <tbody>
        {entries.map(([name, prop], idx) => {
          const isRequired = required.includes(name);
          const isEven = idx % 2 === 0;

          return (
            <React.Fragment key={name}>
              <tr
                style={{
                  backgroundColor: isEven ? '#ffffff' : '#fafafa',
                  borderBottom: '1px solid #f1f5f9',
                }}
              >
                {/* Campo */}
                <td style={{ padding: '9px 12px', verticalAlign: 'top' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <code
                      style={{
                        fontFamily: 'var(--font-mono, monospace)',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#1d4ed8',
                      }}
                    >
                      {name}
                    </code>
                    {prop.readOnly && (
                      <span
                        style={{
                          fontSize: '10px',
                          color: '#6b7280',
                          backgroundColor: '#f3f4f6',
                          padding: '0 4px',
                          borderRadius: '3px',
                        }}
                      >
                        read-only
                      </span>
                    )}
                    {prop.writeOnly && (
                      <span
                        style={{
                          fontSize: '10px',
                          color: '#6b7280',
                          backgroundColor: '#f3f4f6',
                          padding: '0 4px',
                          borderRadius: '3px',
                        }}
                      >
                        write-only
                      </span>
                    )}
                  </div>
                </td>

                {/* Tipo */}
                <td style={{ padding: '9px 12px', verticalAlign: 'top' }}>
                  <TypeBadge prop={prop} />
                  {prop.nullable && (
                    <span style={{ fontSize: '10px', color: '#9ca3af', marginLeft: '4px' }}>
                      nullable
                    </span>
                  )}
                </td>

                {/* Obrigatório */}
                <td style={{ padding: '9px 12px', textAlign: 'center', verticalAlign: 'top' }}>
                  {isRequired ? (
                    <span
                      style={{ color: '#dc2626', fontWeight: 700, fontSize: '14px' }}
                      title="Obrigatório"
                    >
                      ✓
                    </span>
                  ) : (
                    <span style={{ color: '#d1d5db', fontSize: '14px' }}>—</span>
                  )}
                </td>

                {/* Descrição */}
                <td style={{ padding: '9px 12px', verticalAlign: 'top' }}>
                  {prop.description && (
                    <p style={{ margin: 0, color: '#374151', fontSize: '12px', lineHeight: '1.5' }}>
                      {prop.description}
                    </p>
                  )}
                  {prop.enum && prop.enum.length > 0 && (
                    <div>
                      <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600 }}>
                        Valores:{' '}
                      </span>
                      <EnumBadges values={prop.enum} />
                    </div>
                  )}
                  {(prop.minimum !== undefined || prop.maximum !== undefined) && (
                    <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#6b7280' }}>
                      {prop.minimum !== undefined && `min: ${prop.minimum}`}
                      {prop.minimum !== undefined && prop.maximum !== undefined && ' · '}
                      {prop.maximum !== undefined && `max: ${prop.maximum}`}
                    </p>
                  )}
                  {(prop.minLength !== undefined || prop.maxLength !== undefined) && (
                    <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#6b7280' }}>
                      {prop.minLength !== undefined && `minLen: ${prop.minLength}`}
                      {prop.minLength !== undefined && prop.maxLength !== undefined && ' · '}
                      {prop.maxLength !== undefined && `maxLen: ${prop.maxLength}`}
                    </p>
                  )}
                  {prop.pattern && (
                    <p
                      style={{
                        margin: '2px 0 0',
                        fontSize: '11px',
                        color: '#6b7280',
                        fontFamily: 'monospace',
                      }}
                    >
                      pattern: {prop.pattern}
                    </p>
                  )}
                  {prop.default !== undefined && (
                    <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#6b7280' }}>
                      default:{' '}
                      <code style={{ fontFamily: 'monospace' }}>{String(prop.default)}</code>
                    </p>
                  )}
                </td>

                {/* Exemplo */}
                <td style={{ padding: '9px 12px', verticalAlign: 'top' }}>
                  {prop.example !== undefined && (
                    <code
                      style={{
                        fontSize: '11px',
                        color: '#374151',
                        backgroundColor: '#f1f5f9',
                        padding: '1px 6px',
                        borderRadius: '3px',
                        fontFamily: 'var(--font-mono, monospace)',
                        wordBreak: 'break-all',
                      }}
                    >
                      {typeof prop.example === 'object'
                        ? JSON.stringify(prop.example)
                        : String(prop.example)}
                    </code>
                  )}
                </td>
              </tr>

              {/* Sub-tabela para objectos aninhados */}
              {prop.type === 'object' && prop.properties && depth < 2 && (
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <td
                    colSpan={5}
                    style={{ padding: '0 0 0 32px', borderBottom: '1px solid #e5e7eb' }}
                  >
                    <div
                      style={{
                        borderLeft: '3px solid #bfdbfe',
                        paddingLeft: '12px',
                        margin: '8px 0',
                      }}
                    >
                      <p
                        style={{
                          fontSize: '11px',
                          color: '#6b7280',
                          fontWeight: 600,
                          marginBottom: '6px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Propriedades de {name}:
                      </p>
                      <SchemaTable schema={prop as SchemaObject} depth={depth + 1} />
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

// ── Componente de schema completo ─────────────────────────────────────────

function SchemaCard({ name, schema }: { name: string; schema: SchemaObject }) {
  const [expanded, setExpanded] = React.useState(true);

  return (
    <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden mb-3 bg-white dark:bg-slate-900 shadow-xs">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-800/70 text-left cursor-pointer ${
          expanded ? 'border-b border-slate-200 dark:border-slate-800' : ''
        }`}
      >
        <div className="flex items-center gap-2.5 flex-wrap">
          <span className="font-mono text-sm font-bold text-slate-900 dark:text-white">
            {name}
          </span>
          {schema.description && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              — {schema.description}
            </span>
          )}
          <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300 bg-slate-200/70 dark:bg-slate-700 px-2 py-0.5 rounded-full">
            {Object.keys(schema.properties || {}).length} campos
          </span>
        </div>
        <span className="text-slate-400 dark:text-slate-500">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>

      {/* Tabela */}
      {expanded && (
        <div className="overflow-x-auto">
          <SchemaTable schema={schema} />
        </div>
      )}
    </div>
  );
}

// ── Componente de todos os schemas ────────────────────────────────────────

function SchemasSection({ schemas }: { schemas: Record<string, SchemaObject> }) {
  const entries = Object.entries(schemas);
  if (entries.length === 0) return null;

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-3 pb-2.5 border-b-2 border-slate-200 dark:border-slate-800">
        <h3 className="text-base font-bold text-slate-900 dark:text-white m-0 flex items-center gap-2">
          <Layers className="w-4 h-4 text-primary-500" /> Schemas ({entries.length})
        </h3>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          Modelos de dados utilizados pela API
        </span>
      </div>
      {entries.map(([name, schema]) => (
        <SchemaCard key={name} name={name} schema={schema} />
      ))}
    </div>
  );
}

// ── Plugin do Swagger UI ──────────────────────────────────────────────────

/**
 * Cria o plugin do Swagger UI que substitui a secção de schemas
 * por uma apresentação em tabela.
 */
export function createSchemaTablePlugin(spec: any) {
  const schemas = spec?.components?.schemas || {};

  return () => ({
    wrapComponents: {
      // Substituir o componente "Models" (secção de schemas) por tabelas
      Models: () => () => {
        if (Object.keys(schemas).length === 0) return null;
        return <SchemasSection schemas={schemas} />;
      },
    },
  });
}
